import { useEffect } from 'react'
import mapboxgl from 'mapbox-gl'
import {
  calculateDistance,
  getMidpoint,
  extractCoordinates,
} from '../../utils/geometry'
import { getBuildingClassificationColor } from '../../../../../utils/buildingClassificationColors'
import {
  DEFAULT_BUILDING_HEIGHT_M,
  getBuildingExtrusionHeight,
  getBuildingHeightExpression,
  getBuildingGeoJsonHasHeight,
  getBuilding3DColorExpression,
} from './buildingLayerUtils'

interface Layer {
  id: string
  enabled: boolean
  visible?: boolean
}

interface UseBuildingLayersProps {
  map: React.MutableRefObject<mapboxgl.Map | null>
  mapLoaded: boolean
  buildingGeoJson?: any
  buildingHeight?: number
  /** Terrain-relative height for a single building; leaves legacy fallbacks intact. */
  buildingDisplayHeight?: number
  buildingClassification?: string
  buildingProtectedRank?: number
  layers: Layer[]
  styleLoadedCount: number
}

/**
 * Get color based on protected rank
 * @param rank - Protected rank (1-4)
 * @returns CSS color string or undefined if not protected
 */
const getProtectedRankColor = (rank?: number): string | undefined => {
  if (!rank || rank <= 0) return undefined
  switch (rank) {
    case 4:
      return '#ff3b30' // Red - highest protection
    case 3:
      return '#ff9500' // Orange
    case 2:
      return '#ffcc00' // Yellow
    case 1:
      return '#34c759' // Green - lowest protection
    default:
      return undefined
  }
}

/**
 * Hook to handle building layers (footprints, 3D extrusion, and dimension metrics)
 */
export const useBuildingLayers = ({
  map,
  mapLoaded,
  buildingGeoJson,
  buildingHeight,
  buildingDisplayHeight,
  buildingClassification,
  buildingProtectedRank,
  layers,
  styleLoadedCount,
}: UseBuildingLayersProps) => {
  // Handle building footprints rendering from buildingGeoJson
  useEffect(() => {
    if (!map.current || !mapLoaded) return

    // When no building data, clear existing layers
    if (!buildingGeoJson) {
      try {
        if (map.current.getSource('building-footprints-source')) {
          const source = map.current.getSource(
            'building-footprints-source'
          ) as mapboxgl.GeoJSONSource
          source.setData({ type: 'FeatureCollection', features: [] })
        }
      } catch {
        // ignore
      }
      return
    }

    const buildingLayer = layers.find(
      (layer) => layer.id === 'building-footprints'
    )
    const isVisible = buildingLayer?.visible ?? buildingLayer?.enabled ?? false

    const isFeatureCollection = buildingGeoJson?.type === 'FeatureCollection'

    // Per-feature color expression based on classification property
    const classificationColorExpr = [
      'match',
      ['get', 'classification'],
      'Residential',
      '#F9D518',
      'Mixed_1',
      '#F3A83C',
      'Mixed_2',
      '#F3733C',
      'Retail',
      '#D3B39C',
      'Offices',
      '#9ecae1',
      'Industrial',
      '#C39EF9',
      'Public',
      '#8C8C8C',
      'Garage',
      '#bbbbbb',
      'Other',
      '#CCCCCC',
      'Not classified',
      '#CCCCCC',
      'notClassified',
      '#CCCCCC',
      '',
      '#CCCCCC',
      '#CCCCCC',
    ]

    const singleBuildingColor =
      getProtectedRankColor(buildingProtectedRank) ||
      getBuildingClassificationColor(buildingClassification)

    const perFeatureColor = isFeatureCollection
      ? classificationColorExpr
      : singleBuildingColor

    // Per-feature fill opacity: underground=0.05, projected=0.1, normal=0.2
    const perFeatureFillOpacity = isFeatureCollection
      ? [
          'case',
          ['==', ['get', 'is_underground'], true],
          0.05,
          ['==', ['get', 'is_projected'], true],
          0.1,
          0.2,
        ]
      : 0.2

    // Line layer IDs for the 3 stroke types
    const LINE_EXISTING = 'building-footprints-layer'
    const LINE_PROJECTED = 'building-footprints-projected'
    const LINE_UNDERGROUND = 'building-footprints-underground'
    const FILL_LAYER = 'building-footprints-fill'
    const ALL_LINE_LAYERS = [LINE_EXISTING, LINE_PROJECTED, LINE_UNDERGROUND]

    try {
      // Normalize buildingGeoJson: handle Feature, FeatureCollection, or raw geometry
      const normalizedData =
        buildingGeoJson.type === 'Feature' ||
        buildingGeoJson.type === 'FeatureCollection'
          ? buildingGeoJson
          : { type: 'Feature', geometry: buildingGeoJson, properties: {} }

      // Add or update source
      if (!map.current.getSource('building-footprints-source')) {
        map.current.addSource('building-footprints-source', {
          type: 'geojson',
          data: normalizedData,
        })
      } else {
        const source = map.current.getSource(
          'building-footprints-source'
        ) as mapboxgl.GeoJSONSource
        source.setData(normalizedData)
      }

      // --- Fill layer (shared by all buildings) ---
      if (!map.current.getLayer(FILL_LAYER)) {
        map.current.addLayer({
          id: FILL_LAYER,
          type: 'fill',
          source: 'building-footprints-source',
          paint: {
            'fill-color': perFeatureColor as any,
            'fill-opacity': perFeatureFillOpacity as any,
          },
        })
      } else {
        map.current.setPaintProperty(
          FILL_LAYER,
          'fill-color',
          perFeatureColor as any
        )
        map.current.setPaintProperty(
          FILL_LAYER,
          'fill-opacity',
          perFeatureFillOpacity as any
        )
      }

      if (isFeatureCollection) {
        // --- Existing buildings: solid stroke ---
        if (!map.current.getLayer(LINE_EXISTING)) {
          map.current.addLayer({
            id: LINE_EXISTING,
            type: 'line',
            source: 'building-footprints-source',
            filter: [
              'all',
              ['!=', ['get', 'is_projected'], true],
              ['!=', ['get', 'is_underground'], true],
            ],
            layout: { 'line-join': 'round', 'line-cap': 'round' },
            paint: {
              'line-color': classificationColorExpr as any,
              'line-width': 2,
              'line-opacity': 0.9,
            },
          })
        } else {
          map.current.setPaintProperty(
            LINE_EXISTING,
            'line-color',
            classificationColorExpr as any
          )
          map.current.setFilter(LINE_EXISTING, [
            'all',
            ['!=', ['get', 'is_projected'], true],
            ['!=', ['get', 'is_underground'], true],
          ])
        }

        // --- Projected buildings: dashed stroke in classification color ---
        if (!map.current.getLayer(LINE_PROJECTED)) {
          map.current.addLayer({
            id: LINE_PROJECTED,
            type: 'line',
            source: 'building-footprints-source',
            filter: ['==', ['get', 'is_projected'], true],
            layout: { 'line-join': 'round', 'line-cap': 'butt' },
            paint: {
              'line-color': classificationColorExpr as any,
              'line-width': 2,
              'line-opacity': 0.9,
              'line-dasharray': [4, 3],
            },
          })
        } else {
          map.current.setPaintProperty(
            LINE_PROJECTED,
            'line-color',
            classificationColorExpr as any
          )
          map.current.setFilter(LINE_PROJECTED, [
            '==',
            ['get', 'is_projected'],
            true,
          ])
        }

        // --- Underground buildings: dotted stroke in indigo ---
        if (!map.current.getLayer(LINE_UNDERGROUND)) {
          map.current.addLayer({
            id: LINE_UNDERGROUND,
            type: 'line',
            source: 'building-footprints-source',
            filter: ['==', ['get', 'is_underground'], true],
            layout: { 'line-join': 'round', 'line-cap': 'round' },
            paint: {
              'line-color': '#6366f1',
              'line-width': 2,
              'line-opacity': 0.6,
              'line-dasharray': [1, 2],
            },
          })
        } else {
          map.current.setFilter(LINE_UNDERGROUND, [
            '==',
            ['get', 'is_underground'],
            true,
          ])
        }
      } else {
        // Single building: solid stroke, no filters
        if (!map.current.getLayer(LINE_EXISTING)) {
          map.current.addLayer({
            id: LINE_EXISTING,
            type: 'line',
            source: 'building-footprints-source',
            layout: { 'line-join': 'round', 'line-cap': 'round' },
            paint: {
              'line-color': singleBuildingColor,
              'line-width': 2,
              'line-opacity': 0.9,
            },
          })
        } else {
          map.current.setPaintProperty(
            LINE_EXISTING,
            'line-color',
            singleBuildingColor
          )
        }
      }

      // Update visibility for all line layers + fill
      const allLayers = [FILL_LAYER, ...ALL_LINE_LAYERS]
      for (const layerId of allLayers) {
        if (map.current.getLayer(layerId)) {
          map.current.setLayoutProperty(
            layerId,
            'visibility',
            isVisible ? 'visible' : 'none'
          )
        }
      }
    } catch (error) {
      console.warn('Failed to add building footprints:', error)
    }
  }, [
    buildingGeoJson,
    buildingClassification,
    buildingProtectedRank,
    layers,
    mapLoaded,
    styleLoadedCount,
  ])

  // Handle building 3D extrusion
  useEffect(() => {
    if (!map.current || !mapLoaded || !buildingGeoJson) return

    // For FeatureCollection, check if any feature has a height property
    const hasHeight = getBuildingGeoJsonHasHeight(
      buildingGeoJson,
      buildingHeight
    )

    if (!hasHeight) return

    const building3DLayer = layers.find((layer) => layer.id === 'building-3d')
    const isVisible =
      building3DLayer?.visible ?? building3DLayer?.enabled ?? false

    // Per-feature base color for 3D (no alpha — transparency is baked in below)
    const baseColor3D =
      buildingGeoJson.type === 'FeatureCollection'
        ? [
            'match',
            ['get', 'classification'],
            'Residential',
            '#F9D518',
            'Mixed_1',
            '#F3A83C',
            'Mixed_2',
            '#F3733C',
            'Retail',
            '#D3B39C',
            'Offices',
            '#9ecae1',
            'Industrial',
            '#C39EF9',
            'Public',
            '#8C8C8C',
            'Garage',
            '#bbbbbb',
            'Other',
            '#CCCCCC',
            'Not classified',
            '#CCCCCC',
            '#F3733C',
          ]
        : getProtectedRankColor(buildingProtectedRank) ||
          getBuildingClassificationColor(buildingClassification)

    // Bake the per-feature opacity (hide underground, fade projected) into the
    // color alpha. `fill-extrusion-opacity` MUST stay constant — a data-driven
    // value silently kills the whole extrusion layer in Mapbox GL.
    const color3D =
      buildingGeoJson.type === 'FeatureCollection'
        ? getBuilding3DColorExpression(baseColor3D)
        : baseColor3D
    const opacity3D = buildingGeoJson.type === 'FeatureCollection' ? 1 : 0.7

    // Use per-feature height from properties if available, otherwise use the prop
    const heightExpr =
      buildingGeoJson.type === 'FeatureCollection'
        ? getBuildingHeightExpression(
            buildingHeight || DEFAULT_BUILDING_HEIGHT_M
          )
        : getBuildingExtrusionHeight(
            buildingGeoJson,
            buildingHeight,
            buildingDisplayHeight
          )

    try {
      // Add layer if it doesn't exist
      if (!map.current.getLayer('building-3d-layer')) {
        map.current.addLayer({
          id: 'building-3d-layer',
          type: 'fill-extrusion',
          source: 'building-footprints-source',
          paint: {
            'fill-extrusion-color': color3D as any,
            'fill-extrusion-height': heightExpr as any,
            'fill-extrusion-base': 0,
            'fill-extrusion-opacity': opacity3D as any,
          },
        })
      } else {
        // Update color/opacity if data changed
        map.current.setPaintProperty(
          'building-3d-layer',
          'fill-extrusion-color',
          color3D as any
        )
        map.current.setPaintProperty(
          'building-3d-layer',
          'fill-extrusion-opacity',
          opacity3D as any
        )
        map.current.setPaintProperty(
          'building-3d-layer',
          'fill-extrusion-height',
          heightExpr as any
        )
      }

      // Update visibility based on layer state
      if (map.current.getLayer('building-3d-layer')) {
        map.current.setLayoutProperty(
          'building-3d-layer',
          'visibility',
          isVisible ? 'visible' : 'none'
        )
      }
    } catch (error) {
      console.warn('Failed to add building 3D extrusion:', error)
    }
  }, [
    buildingGeoJson,
    buildingHeight,
    buildingDisplayHeight,
    buildingClassification,
    buildingProtectedRank,
    layers,
    mapLoaded,
    styleLoadedCount,
  ])

  // Handle dimension metrics for building
  useEffect(() => {
    if (!map.current || !mapLoaded || !buildingGeoJson) return

    const dimensionLayer = layers.find(
      (layer) => layer.id === 'building-dimension-metrics'
    )
    const isVisible =
      dimensionLayer?.visible ?? dimensionLayer?.enabled ?? false

    const buildingColor =
      getProtectedRankColor(buildingProtectedRank) ||
      getBuildingClassificationColor(buildingClassification)

    try {
      // Collect all geometries to extract coordinates from
      const geometries: any[] = []
      if (buildingGeoJson.type === 'FeatureCollection') {
        buildingGeoJson.features.forEach((f: any) => {
          if (f.geometry) geometries.push(f.geometry)
        })
      } else if (buildingGeoJson.type === 'Feature') {
        if (buildingGeoJson.geometry) geometries.push(buildingGeoJson.geometry)
      } else {
        geometries.push(buildingGeoJson)
      }

      // Create dimension labels for each building geometry
      const dimensionLabels: any[] = []
      for (const geo of geometries) {
        const coordinates = extractCoordinates(geo)
        if (coordinates.length === 0) continue

        const outerRing = coordinates[0]
        for (let i = 0; i < outerRing.length - 1; i++) {
          const start = outerRing[i]
          const end = outerRing[i + 1]
          const distance = calculateDistance(start, end)
          const midpoint = getMidpoint(start, end)

          dimensionLabels.push({
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: midpoint,
            },
            properties: {
              distance: distance.toFixed(2),
              distanceLabel: `${distance.toFixed(1)}m`,
            },
          })
        }
      }

      // Add building dimension metrics source
      if (!map.current.getSource('building-dimension-metrics-source')) {
        map.current.addSource('building-dimension-metrics-source', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: dimensionLabels,
          },
        })
      } else {
        // Update existing source
        const source = map.current.getSource(
          'building-dimension-metrics-source'
        ) as mapboxgl.GeoJSONSource
        source.setData({
          type: 'FeatureCollection',
          features: dimensionLabels,
        })
      }

      // Add or remove building dimension metrics layer based on visibility
      const layerExists = map.current.getLayer(
        'building-dimension-metrics-layer'
      )

      if (isVisible && !layerExists) {
        // Add layer when it should be visible and doesn't exist
        map.current.addLayer({
          id: 'building-dimension-metrics-layer',
          type: 'symbol',
          source: 'building-dimension-metrics-source',
          layout: {
            'text-field': ['get', 'distanceLabel'],
            'text-size': 12,
            'text-offset': [0, 0],
            'text-anchor': 'center',
            'text-allow-overlap': true,
          },
          paint: {
            'text-color': buildingColor,
            'text-halo-color': '#FFFFFF',
            'text-halo-width': 2,
          },
        })
      } else if (isVisible && layerExists) {
        // Update color if layer already exists and should be visible
        map.current.setPaintProperty(
          'building-dimension-metrics-layer',
          'text-color',
          buildingColor
        )
      } else if (!isVisible && layerExists) {
        // Remove layer when it should be hidden and exists
        map.current.removeLayer('building-dimension-metrics-layer')
      }
    } catch (error) {
      console.warn('Failed to add building dimension metrics:', error)
    }
  }, [
    buildingGeoJson,
    buildingClassification,
    buildingProtectedRank,
    layers,
    mapLoaded,
    styleLoadedCount,
  ])

  // Handle building number labels at polygon centroids
  useEffect(() => {
    if (!map.current || !mapLoaded) return

    const sourceId = 'building-number-labels-source'
    const layerId = 'building-number-labels-layer'

    const labelsLayer = layers.find(
      (layer) => layer.id === 'building-number-labels'
    )
    const isVisible = labelsLayer?.visible ?? labelsLayer?.enabled ?? false

    // When not visible, remove the layer and source entirely
    if (!isVisible) {
      try {
        if (map.current.getLayer(layerId)) {
          map.current.removeLayer(layerId)
        }
        if (map.current.getSource(sourceId)) {
          map.current.removeSource(sourceId)
        }
      } catch {
        // ignore
      }
      return
    }

    // Only create for FeatureCollections with buildingLabel properties
    if (!buildingGeoJson) return
    if (buildingGeoJson.type !== 'FeatureCollection') return

    const features = buildingGeoJson.features?.filter(
      (f: any) => f.properties?.buildingLabel
    )
    if (!features || features.length === 0) return

    try {
      // Compute centroid for each building polygon
      const labelFeatures = features
        .map((f: any) => {
          const coords = f.geometry?.coordinates
          if (!coords) return null

          const ring =
            f.geometry.type === 'MultiPolygon' ? coords[0]?.[0] : coords[0]
          if (!ring || ring.length === 0) return null

          let sumLng = 0
          let sumLat = 0
          const len = ring.length - 1
          for (let i = 0; i < len; i++) {
            sumLng += ring[i][0]
            sumLat += ring[i][1]
          }

          return {
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [sumLng / len, sumLat / len],
            },
            properties: {
              label: f.properties.buildingLabel,
            },
          }
        })
        .filter(Boolean)

      if (!map.current.getSource(sourceId)) {
        map.current.addSource(sourceId, {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: labelFeatures },
        })
      } else {
        ;(map.current.getSource(sourceId) as mapboxgl.GeoJSONSource).setData({
          type: 'FeatureCollection',
          features: labelFeatures,
        })
      }

      if (!map.current.getLayer(layerId)) {
        map.current.addLayer({
          id: layerId,
          type: 'symbol',
          source: sourceId,
          layout: {
            'text-field': ['get', 'label'],
            'text-size': 12,
            'text-anchor': 'center',
            'text-allow-overlap': true,
          },
        })
      }
    } catch (error) {
      console.warn('Failed to add building number labels:', error)
    }
  }, [buildingGeoJson, layers, mapLoaded, styleLoadedCount])
}
